import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Autocomplete, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { useActionHook, useCanCreateDeal, usePrompt } from "hooks";
import { useDealActions } from "store";
import { IDealsTableRow } from "store/types/deal/deal";

import { CreateDealContent } from "../../Prompts/CreateDealContent";

export interface SelectSecurityResult {
  dealId: number;
  roundName: string;
}

interface SelectSecurityProps {
  onCancel: () => void;
  onSubmit: (result: SelectSecurityResult | null) => void;
  deal: number | null;
  dealList: IDealsTableRow[];
  refetchDeals: () => void;
}

export const SelectSecurity: React.FC<SelectSecurityProps> = ({ onCancel, onSubmit, deal, dealList, refetchDeals }) => {
  const [selectedDeal, setSelectedDeal] = useState<number | null>(deal ?? null);
  const [localDealList, setLocalDealList] = useState<IDealsTableRow[]>(dealList ?? []);
  const [selectedRoundName, setSelectedRoundName] = useState<string>("");
  const { getRoundsAction } = useDealActions();
  const { action: fetchRounds, data: rounds = [] } = useActionHook(getRoundsAction);
  const openPrompt = usePrompt();
  const canCreateDeal = useCanCreateDeal();

  useEffect(() => {
    if (selectedDeal) {
      fetchRounds({ dealId: selectedDeal, with_commitments: false });
      setSelectedRoundName("");
    }
  }, [selectedDeal]);

  const roundNames = useMemo(() => rounds.map((r) => r.name), [rounds]);

  const handleCreateNewSecurity = useCallback(async () => {
    const data = await openPrompt({
      Component: CreateDealContent
    });

    if (data) {
      refetchDeals();
      setLocalDealList([...localDealList, { id: data.dealId, title: data.dealTitle } as IDealsTableRow]);
      setSelectedDeal(data.dealId);
    }
  }, [openPrompt]);

  const selectedOption = useMemo(() => {
    return localDealList?.find((deal) => deal.id === selectedDeal);
  }, [localDealList, selectedDeal]);

  const canApply = selectedDeal !== null && selectedRoundName.trim() !== "";

  return (
    <Stack sx={{ borderRadius: "8px", boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)", padding: "20px" }} gap="20px">
      <Typography>Select Altius Security Name</Typography>
      <Stack direction="row" gap="20px" alignItems="center" flexWrap="wrap">
        <Autocomplete
          key={selectedOption?.id}
          disableClearable
          sx={{ width: "300px" }}
          options={localDealList ?? []}
          value={selectedOption}
          getOptionLabel={(option) => option.title}
          onChange={(_, value) => setSelectedDeal(value?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Altius Security Name" placeholder="Search..." />}
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase();
            return options.filter((option) => option.title.toLowerCase().includes(searchTerm));
          }}
          slotProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [
                {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                    rootBoundary: "viewport",
                    padding: 8
                  }
                }
              ]
            }
          }}
          PaperComponent={({ children, ...props }) => {
            return (
              <Paper
                sx={{
                  width: "305px",
                  backgroundColor: "white",
                  zIndex: "10",
                  borderRadius: "10px",
                  "& .MuiAutocomplete-listbox": {
                    "& .MuiAutocomplete-option[aria-disabled='true']": {
                      opacity: 1,
                      "&.Mui-focused": {
                        opacity: 1
                      }
                    },
                    "& .MuiAutocomplete-option[aria-selected='true']": {
                      bgcolor: "#edf1f9",
                      "&.Mui-focused": {
                        bgcolor: "#edf1f9"
                      }
                    }
                  },
                  "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                    bgcolor: "#edf1f9"
                  }
                }}
                {...props}
              >
                <>
                  {children}
                  {canCreateDeal && (
                    <Button
                      onMouseDown={handleCreateNewSecurity}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        height: "50px",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                        borderRadius: "0 0 10px 10px"
                      }}
                      fullWidth
                    >
                      Add Deal
                    </Button>
                  )}
                </>
              </Paper>
            );
          }}
        />
        <Autocomplete
          freeSolo
          sx={{ width: "300px" }}
          options={roundNames}
          value={selectedRoundName}
          onInputChange={(_, value) => setSelectedRoundName(value ?? "")}
          onChange={(_, value) => setSelectedRoundName(typeof value === "string" ? value : (value ?? ""))}
          renderInput={(params) => (
            <TextField {...params} label="Round name" placeholder="Select or type round name..." />
          )}
          disabled={!selectedDeal}
        />

        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            borderColor: "#ddd",
            color: "#333",
            "&:hover": {
              borderColor: "#adb5bd",
              backgroundColor: "#f8f9fa"
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            if (canApply) {
              onSubmit({ dealId: selectedDeal!, roundName: selectedRoundName.trim() });
            }
          }}
          variant="contained"
          disabled={!canApply}
        >
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};
